/* Ground Central — legal documents (styles in gc-legal.css).

   Shopify renders /policies/* itself, so the theme can't template those pages.
   This script finishes them, and the Mobile App Privacy Policy page, which
   prints the same markup:
   - puts the document switcher under the title (on policy pages it comes from
     the <template id="GcLegalNav"> that snippets/gc-legal-head.liquid prints,
     along with the label above the title in its data-eyebrow);
   - lifts Shopify's "Last updated: …" opening paragraph up under the title;
   - builds an "On this page" contents list from the h2s, with the section
     being read highlighted. It is a sticky rail at 990px and up, and a
     fold-out above the document below that. */
(() => {
  const DESKTOP = window.matchMedia('(min-width: 990px)');
  const SPY_OFFSET = 140;
  const MIN_SECTIONS = 4;

  const slug = (text) =>
    text
      .toLowerCase()
      .normalize('NFKD')
      .replace(/\p{M}/gu, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'section';

  // "SECTION 4 - PRICES AND BILLING" -> "4. Prices and billing". Shopify's
  // terms template shouts its headings; the contents list reads better quiet.
  const label = (text) => {
    let t = text.replace(/\s+/g, ' ').trim();
    let num = '';
    const m = t.match(/^section\s+(\d+)\s*[-\u2013\u2014:]\s*(.+)$/i);
    if (m) {
      num = `${m[1]}. `;
      t = m[2];
    }
    if (t === t.toUpperCase()) {
      t = t.charAt(0) + t.slice(1).toLowerCase();
      t = t.replace(/\bshopify\b/g, 'Shopify');
    }
    return num + t;
  };

  const chevron =
    '<svg class="gc-legal-toc__chev" width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M1 1.5 6 6.5l5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  const init = () => {
    const root = document.querySelector('.shopify-policy__container');
    if (!root || root.hasAttribute('data-gc-legal-ready')) return;
    root.setAttribute('data-gc-legal-ready', '');

    const title = root.querySelector('.shopify-policy__title');
    const body = root.querySelector('.shopify-policy__body');
    const rte = body && body.querySelector('.rte');
    const h1 = title && title.querySelector('h1');
    if (!h1 || !rte) return;

    const tpl = document.getElementById('GcLegalNav');
    if (tpl && !title.querySelector('.gc-legal-nav')) title.appendChild(tpl.content.cloneNode(true));
    if (tpl && tpl.dataset.eyebrow && !title.dataset.eyebrow) title.dataset.eyebrow = tpl.dataset.eyebrow;

    const first = rte.querySelector('p');
    if (
      first &&
      !title.querySelector('.gc-legal__meta') &&
      /^\s*last updated\b/i.test(first.textContent) &&
      rte.textContent.trim().startsWith(first.textContent.trim())
    ) {
      first.classList.add('gc-legal__meta');
      h1.after(first);
    }

    const heads = [...rte.querySelectorAll('h2')].filter((h) => h.textContent.trim());
    if (heads.length < MIN_SECTIONS) return;

    const toc = document.createElement('details');
    toc.className = 'gc-legal-toc';
    const summary = document.createElement('summary');
    summary.innerHTML = `<span>On this page</span>${chevron}`;
    const nav = document.createElement('nav');
    nav.setAttribute('aria-label', 'On this page');
    const ol = document.createElement('ol');

    const links = heads.map((h) => {
      if (!h.id) {
        const base = slug(h.textContent);
        let id = base;
        for (let n = 2; document.getElementById(id); n++) id = `${base}-${n}`;
        h.id = id;
      }
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = `#${h.id}`;
      a.textContent = label(h.textContent);
      li.appendChild(a);
      ol.appendChild(li);
      return a;
    });

    nav.appendChild(ol);
    toc.append(summary, nav);
    body.before(toc);
    root.classList.add('gc-legal--toc');

    // Open rail on desktop (the summary is only a label there); closed fold-out below.
    const sync = () => {
      toc.open = DESKTOP.matches;
    };
    sync();
    DESKTOP.addEventListener('change', sync);
    summary.addEventListener('click', (event) => {
      if (DESKTOP.matches) event.preventDefault();
    });
    ol.addEventListener('click', (event) => {
      if (event.target.closest('a') && !DESKTOP.matches) toc.open = false;
    });

    // Highlight the section being read, and keep it in view in a long rail.
    let current = null;
    const setCurrent = (a) => {
      if (a === current) return;
      if (current) current.removeAttribute('aria-current');
      current = a;
      if (!a) return;
      a.setAttribute('aria-current', 'true');
      if (DESKTOP.matches && toc.scrollHeight > toc.clientHeight) {
        const r = a.getBoundingClientRect();
        const t = toc.getBoundingClientRect();
        if (r.top < t.top || r.bottom > t.bottom) toc.scrollTop += r.top - t.top - t.height / 2;
      }
    };
    let ticking = false;
    const spy = () => {
      ticking = false;
      let active = -1;
      for (let i = 0; i < heads.length; i++) {
        if (heads[i].getBoundingClientRect().top <= SPY_OFFSET) active = i;
        else break;
      }
      setCurrent(active >= 0 ? links[active] : null);
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(spy);
        }
      },
      { passive: true }
    );
    spy();

    // The ids didn't exist when the browser tried to follow a #section link.
    if (location.hash) {
      const target = document.getElementById(decodeURIComponent(location.hash.slice(1)));
      if (target && rte.contains(target)) target.scrollIntoView();
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
